import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';
import PropTypes from 'prop-types';

import {
  Cancel as CancelIcon,
  ContentCopyOutlined as ContentCopyOutlinedIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  OpenInNew as OpenInNewIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

import { deleteActionAlert, fetchActionAlert, stopActionAlert } from 'store/actions/campaigns';
import { campaignsSelectors } from 'store/selectors/campaigns';
import { setSnackbarError, setSnackbarSuccess, setSnackbarWarning } from 'store/actions/global';

import { cancellationStatuses, LIVE_PUBLISH_STATUS, statuses } from 'utils/constants';
import { useQuery } from 'utils/helpers';
import { renderCreatedBy, renderSendTime } from 'utils/messages';
import { timeFormatOptions } from 'utils/timezones';

import TopCard from 'common/containers/TopCard';

const CircleBack = styled.span`
  cursor: pointer;
`;

const chipColor = (value, sendAtUtc) => {
  const { draft, published } = statuses;

  switch (value) {
    case published: {
      const sent = new Date().getTime() - new Date(sendAtUtc).getTime() > 0;

      return sent ? 'secondary' : 'blue';
    }
    case draft:
      return 'grey';
    default:
      return null;
  }
};

const chipLabel = (value, sendAtUtc) => {
  const { draft, published } = statuses;

  switch (value) {
    case published: {
      const sent = new Date().getTime() - new Date(sendAtUtc).getTime() > 0;

      return sent ? 'Sent' : 'Scheduled';
    }
    case draft:
      return 'Draft';
    default:
      return null;
  }
};

const ActionAlertTopCard = ({ handleOpenParentActionAlert }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const confirm = useConfirm();
  const { draft, published } = statuses;
  const { probablyCancelled } = cancellationStatuses;
  const { actionAlertUuid, actionCenterUuid, campaignUuid, organizationUuid } = useParams();
  const backUrl = useQuery().get('backUrl');

  const { actionAlertDetails, actionCenterDetails } = useSelector(state => ({
    actionAlertDetails: campaignsSelectors.getActionAlertDetails(state),
    actionCenterDetails: campaignsSelectors.getActionCenterDetails(state),
  }));

  const {
    canCreateCircleBack,
    contactLists,
    createdAt,
    createdBy,
    isCancellable,
    message,
    name,
    parent,
    sendAt,
    sendAtUtc,
    status,
    subject,
    type,
    updatedAt,
  } = actionAlertDetails;

  const [isMessageOpen, setMessageOpen] = useState(false);

  const handleMessageClose = () => setMessageOpen(false);

  const handleActionAlertDelete = () => {
    confirm({
      confirmationText: 'Delete',
      description: 'You won`t be able to undo that',
      title: 'Delete the Action Alert?',
    })
      .then(() => dispatch(deleteActionAlert(actionAlertUuid)))
      .then(() =>
        history.push(
          `/${organizationUuid}/campaigns/${campaignUuid}/action-centers/${actionCenterUuid}?tab=0`
        )
      );
  };

  const handleDuplicateActionAlert = () => {
    history.push(
      `/${organizationUuid}/${campaignUuid}/${actionCenterUuid}/create-email-action-alert/duplicate/${actionAlertUuid}${parent ? '?circle-back=true' : ''}`
    );
  };

  const handleEditActionAlert = () => {
    history.push(
      `/${organizationUuid}/${campaignUuid}/${actionCenterUuid}/create-email-action-alert/edit/${actionAlertUuid}${parent ? '?circle-back=true' : ''}`
    );
  };

  const handleCircleBack = () => {
    history.push(
      `/${organizationUuid}/${campaignUuid}/${actionCenterUuid}/create-email-action-alert/circle-back/${actionAlertUuid}`
    );
  };

  const handleStopActionAlert = () => {
    dispatch(stopActionAlert(actionAlertUuid))
      .then(({ message: responseMessage, status: stopedActionAlertStatus }) => {
        if (stopedActionAlertStatus === probablyCancelled) {
          dispatch(setSnackbarWarning({ message: responseMessage }));
        } else {
          dispatch(setSnackbarSuccess({ message: responseMessage }));
        }
        dispatch(fetchActionAlert(actionAlertUuid));
      })
      .catch(({ message: responseMessage }) =>
        dispatch(setSnackbarError({ message: responseMessage }))
      );
  };

  const handleConfirmStopActionAlert = () => {
    confirm({
      cancellationText: 'No',
      confirmationText: 'Yes, Cancel',
      description: '',
      title: 'Cancel Send the Action Alert?',
    }).then(() => handleStopActionAlert(actionAlertUuid));
  };

  const topCardData = useCallback(() => {
    const chipData = {
      chipColor: chipColor(status, sendAtUtc),
      chipLabel: chipLabel(status, sendAtUtc),
      chipOptions: [],
      chipOptionsHeader: 'Change status',
    };

    const actionButtons = [
      {
        color: 'grey',
        disabled: status !== draft,
        name: 'Edit',
        onClick: handleEditActionAlert,
        startIcon: <EditOutlinedIcon />,
      },
      {
        color: 'grey',
        disabled: actionCenterDetails?.publishStatus !== LIVE_PUBLISH_STATUS,
        name: 'Duplicate',
        onClick: handleDuplicateActionAlert,
        startIcon: <ContentCopyOutlinedIcon />,
      },
      {
        color: 'grey',
        name: 'Delete',
        onClick: handleActionAlertDelete,
        startIcon: <DeleteOutlineOutlinedIcon />,
      },
      {
        color: 'grey',
        disabled:
          type !== 'email' ||
          status !== published ||
          !canCreateCircleBack ||
          actionCenterDetails?.publishStatus !== LIVE_PUBLISH_STATUS,
        name: 'Circle Back',
        onClick: handleCircleBack,
        startIcon: <RestoreIcon />,
      },
    ];

    if (isCancellable) {
      actionButtons.unshift({
        color: 'grey',
        disabled: false, // TODO adapt this based on scheduled time
        name: 'Cancel send',
        onClick: handleConfirmStopActionAlert,
        startIcon: <CancelIcon />,
      });
    }

    const additionalActionButtons = [
      {
        color: 'primary',
        endIcon: <OpenInNewIcon />,
        name: 'Preview message',
        onClick: () => setMessageOpen(true),
      },
    ];

    const details = [
      {
        label: 'Subject',
        value: subject,
      },
      ...renderSendTime(status, sendAt, sendAtUtc, updatedAt),
      {
        label: 'Created at',
        value: `${new Date(createdAt).toLocaleString('us-US', timeFormatOptions)}`,
      },
      ...renderCreatedBy(createdBy),
      {
        label: 'Lists',
        value: contactLists?.map(({ name: listName }) => listName).join(', '),
      },
    ];

    const backButtonLink =
      backUrl ||
      `/${organizationUuid}/campaigns/${campaignUuid}/action-centers/${actionCenterUuid}?tab=0`;

    return {
      actionButtons,
      additionalActionButtons,
      backButtonLink,
      chipData,
      details,
      entityTypes: [
        'Action Alert',
        'Email Message',
        parent && <CircleBack onClick={handleOpenParentActionAlert}>Circle Back</CircleBack>,
      ],
      title: name || '',
    };
  }, [name, status, draft, isCancellable, probablyCancelled, organizationUuid]);

  return (
    <>
      <TopCard {...topCardData()} />
      <Dialog
        aria-labelledby="max-width-dialog-message"
        maxWidth="lg"
        open={isMessageOpen}
        onClose={handleMessageClose}
      >
        <DialogTitle id="max-width-dialog-message">{subject}</DialogTitle>
        <DialogContent dangerouslySetInnerHTML={{ __html: message }} />
        <DialogActions>
          <Button onClick={handleMessageClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ActionAlertTopCard.propTypes = {
  handleOpenParentActionAlert: PropTypes.func,
};

export default ActionAlertTopCard;
